import { Dialog } from '@headlessui/react';
import { forwardRef, JSX, useState } from 'react';

import { CloseIcon } from '~components/icons';
import useLabels from '~hooks/useLabels';
import { AssetId } from '~modules/media-library/model/Asset';
import { DownloadFormats } from '~modules/media-library/model/DownloadFormat';
import { DownloadVariations } from '~modules/media-library/model/DownloadVariation';
import { Button } from '~modules/media-library/view/Button';
import { RestrictionCheckbox } from '~modules/media-library/view/RestrictionCheckbox';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';
import { ShareFormatListItem } from '~modules/media-library/view/ShareModal/ShareFormatListItem';
import { ShareFormatPlaceholder } from '~modules/media-library/view/ShareModal/ShareFormatPlaceholder';
import { ShareVariationListItem } from '~modules/media-library/view/ShareModal/ShareVariationListItem';

type Props = {
  assetId: AssetId;
  downloadFormats: DownloadFormats;
  downloadVariations: DownloadVariations;
  onClose: () => void;
  hasRestrictions?: boolean;
  shareAction: ShareAction;
};

export const ShareModalPanel = forwardRef<HTMLDivElement, JSX.IntrinsicElements['div'] & Props>(
  function ShareModalPanel(
    {
      assetId,
      downloadFormats,
      downloadVariations,
      onClose,
      className,
      hasRestrictions = false,
      shareAction,
      ...TheirProps
    },
    ref,
  ) {
    // state
    const [hasAcceptedRestrictions, setHasAcceptedRestrictions] = useState(false);

    // labels
    const { hlMediaLibraryDownloadModal, hlMediaLibraryAddToMyFilesModal } = useLabels([
      'hlMediaLibraryDownloadModal',
      'hlMediaLibraryAddToMyFilesModal',
    ]);

    // Derived values
    const hasDownloadFormats = !!downloadFormats && Object.values(downloadFormats).length > 0;
    const hasDownloadVariations = !!downloadVariations && Object.values(downloadVariations).length > 0;
    const downloadDisabled = hasRestrictions && !hasAcceptedRestrictions;

    return (
      <div
        className={`ml--download-modal--panel bg-white shadow-xl overflow-hidden w-full max-w-xl min-h-0 max-h-full flex flex-col relative transition-all transform rounded-md ${
          className ? className : ''
        }`}
        {...TheirProps}
        ref={ref}
      >
        <header className="ml--download-modal--panel-header flex px-3 py-2 border-solid border-gray border-b-2">
          <Dialog.Title
            as="h2"
            className="ml--download-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0"
          >
            {shareAction === ShareAction.AddToMyfiles ? hlMediaLibraryAddToMyFilesModal : null}
            {shareAction === ShareAction.Download ? hlMediaLibraryDownloadModal : null}
          </Dialog.Title>
        </header>
        <Button variant="icon" className="absolute top-0 right-0 border-none" onClick={onClose}>
          <CloseIcon />
        </Button>
        <div className="flex flex-col gap-3 p-3">
          {hasRestrictions ? (
            <div>
              <RestrictionCheckbox
                isChecked={hasAcceptedRestrictions}
                onCheck={() => setHasAcceptedRestrictions(true)}
              />
            </div>
          ) : null}
          {hasDownloadFormats ? (
            <>
              {Object.values(downloadFormats).map(downloadFormat => (
                <ShareFormatListItem
                  assetId={assetId}
                  downloadFormat={downloadFormat}
                  key={downloadFormat.key}
                  disabled={downloadDisabled}
                  shareAction={shareAction}
                />
              ))}
            </>
          ) : (
            <>
              <ShareFormatPlaceholder />
              <ShareFormatPlaceholder />
              <ShareFormatPlaceholder />
              <ShareFormatPlaceholder />
            </>
          )}
          {hasDownloadVariations && !downloadDisabled ? (
            <>
              <h5>Editable InDesign files</h5>
              {Object.values(downloadVariations).map(downloadVariation => (
                <ShareVariationListItem
                  key={'ShareVariationListItem' + downloadVariation.id}
                  downloadVariation={downloadVariation}
                  disabled={downloadDisabled}
                  shareAction={shareAction}
                />
              ))}
            </>
          ) : null}
        </div>
      </div>
    );
  },
);
